import { useEffect, useState } from "react";
import { submitWaitlist } from "../lib/waitlist";

type FormStatus = "idle" | "loading" | "success" | "error" | "already-signed-up";

const STORAGE_KEY = "waitlist-signup";

export default function MinimalLandingPage() {
  const [learningGoal, setLearningGoal] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<FormStatus>("idle");
  const [errorMessage, setErrorMessage] = useState("");

  // Force the document into dark mode while this page is mounted — the
  // shared index.css paints a light radial gradient on <body> that we
  // need to override. Legacy Lingual green branding is intentionally
  // not used on this page.
  useEffect(() => {
    const originalBodyBackground = document.body.style.background;
    const originalBodyColor = document.body.style.color;
    const originalHtmlColorScheme = document.documentElement.style.colorScheme;

    document.body.style.background = "#000";
    document.body.style.color = "#fff";
    document.documentElement.style.colorScheme = "dark";

    return () => {
      document.body.style.background = originalBodyBackground;
      document.body.style.color = originalBodyColor;
      document.documentElement.style.colorScheme = originalHtmlColorScheme;
    };
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.email) {
          setEmail(parsed.email);
          setStatus("already-signed-up");
        }
      }
    } catch {
      // Private browsing or corrupted data — ignore.
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedEmail = email.trim();
    const trimmedGoal = learningGoal.trim();

    if (!/.+@.+\..+/.test(trimmedEmail)) {
      setStatus("error");
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    setStatus("loading");
    setErrorMessage("");

    const result = await submitWaitlist({
      email: trimmedEmail,
      native_languages: [],
      target_languages: [],
      learning_goal: trimmedGoal || undefined,
    });

    if ("success" in result) {
      setStatus("success");
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ email: trimmedEmail, signedUpAt: new Date().toISOString() })
        );
      } catch {
        // Private browsing — ignore.
      }
    } else {
      setStatus("error");
      setErrorMessage(result.error);
    }
  };

  const handleReset = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Private browsing — ignore.
    }
    setLearningGoal("");
    setEmail("");
    setStatus("idle");
    setErrorMessage("");
  };

  const isSubmitted = status === "success" || status === "already-signed-up";
  const canSubmit = email.trim() !== "" && status !== "loading";

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-xl">
        <header className="text-center mb-10">
          <h1 className="text-6xl md:text-7xl font-semibold tracking-tight text-white">Lingual</h1>
          <p className="mt-6 text-sm md:text-[15px] text-white/55 leading-relaxed max-w-lg mx-auto text-left">
            Big Tech has a thesis — your life will be easy. You&apos;ll watch what your algorithm
            tells you to. You&apos;ll rent intelligence from them. That the experiences that make
            you human — trying, failing, learning, and trying again — will be a thing of the past.
            But you&apos;re more than a consumer or a data point. You deserve to learn what you
            want, and to be pushed further than you thought possible.
          </p>
        </header>

        {isSubmitted ? (
          <div className="max-w-md mx-auto text-center space-y-3">
            <p className="text-sm text-white/80">
              {status === "success"
                ? "You\u2019re on the list."
                : "You\u2019re already on the list."}
            </p>
            <p className="text-xs text-white/40">{email}</p>
            <button
              type="button"
              onClick={handleReset}
              className="text-xs text-white/50 hover:text-white/80 underline underline-offset-4 transition-colors"
            >
              Use a different email
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="max-w-md mx-auto space-y-3">
            {status === "error" && errorMessage && (
              <div
                role="alert"
                className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300"
              >
                {errorMessage}
              </div>
            )}

            <label className="block">
              <span className="sr-only">What do you want to learn?</span>
              <textarea
                value={learningGoal}
                onChange={(e) => setLearningGoal(e.target.value)}
                placeholder="What do you want to learn?"
                rows={2}
                maxLength={500}
                disabled={status === "loading"}
                className="w-full resize-none rounded-md border border-white/15 bg-white/[0.03] px-3 py-2.5 text-sm text-white placeholder:text-white/35 transition-colors hover:border-white/25 focus:border-white/40 focus:bg-white/[0.05] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              />
            </label>

            <label className="block">
              <span className="sr-only">Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                disabled={status === "loading"}
                autoComplete="email"
                className="w-full rounded-md border border-white/15 bg-white/[0.03] px-3 py-2.5 text-sm text-white placeholder:text-white/35 transition-colors hover:border-white/25 focus:border-white/40 focus:bg-white/[0.05] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
              />
            </label>

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full rounded-md bg-white px-4 py-2.5 text-sm font-medium text-black transition-colors hover:bg-white/90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {status === "loading" ? "Joining\u2026" : "Join Waitlist"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
